import { AgeGroups, GameModes, OrderByDirections, PeriodType, PlayPositions, PlaySessionTypes, ScoreFieldTypes, ScoreTypes, TimePeriods } from "../../enums";
import { boolOrIntCodec, timestampCodec } from "../../validation/codecs";
import { enumCodec, mixed, optional, required, t } from "../../validation/validator";

/* ========= User Stats ========= */

const UserStatsOrderByFieldSchema = t.union([
  t.literal("ended_at"),
  t.literal("created_at"),
  t.literal("updated_at"),
]);

export const GetUserStatsRequestSchema = mixed([
  required({
    user_ids: t.array(t.string),
    stats: t.array(
      required({
        score_name: t.string,
        score_field: enumCodec(ScoreFieldTypes),
        score_type: enumCodec(ScoreTypes),
      })
    ),
  }),
  optional({
    from_date: timestampCodec,
    to_date: timestampCodec,
    play_position: enumCodec(PlayPositions),
    game_mode: enumCodec(GameModes),
    session_type: enumCodec(PlaySessionTypes),
    is_completed: boolOrIntCodec,
    offset: t.number,
    limit: t.number,
    group_by_period: enumCodec(TimePeriods),
    order_by: required({
      field: UserStatsOrderByFieldSchema,
      direction: enumCodec(OrderByDirections),
    }),
  }),
]);

const UserStatsSchema = t.intersection([
  t.type({
    id: t.string,
    user_id: t.string,
  }),
  t.partial({
    scan_score: t.number,
    scan_rate: t.number,
    scan_timing: t.number,
    passes_score: t.number,
    passes_made: t.number,
    passes_succeeded: t.number,
    passes_packing_rate: t.number,
    critical_scans: t.number,
    critical_scan_score: t.number,
    shots_made: t.number,
    shots_succeeded: t.number,
    tackles_made: t.number,
    tackles_succeeded: t.number,
    labels_made: t.number,
    labels_succeeded: t.number,
    receivals_made: t.number,
    receivals_succeeded: t.number,
    total_score: t.number,
    byb_iq: t.number,
    awareness_score: t.number,
    gameplay_duration: t.number,
    year: t.number,
    month: t.number,
    week: t.number,
    day: t.number,
  }),
]);
export const GetUserStatsResponseSchema = t.array(UserStatsSchema);

export type GetUserStatsRequest = t.TypeOf<typeof GetUserStatsRequestSchema>;
export type UserStats = t.TypeOf<typeof UserStatsSchema>;
export type GetUserStatsResponse = t.TypeOf<typeof GetUserStatsResponseSchema>;

export const GetLeaderboardRequestSchema = mixed([
  required({
    score_field: enumCodec(ScoreFieldTypes),
    score_type: enumCodec(ScoreTypes),
  }),
  optional({
    from_date: timestampCodec,
    to_date: timestampCodec,
    previous_date: timestampCodec,
    offset: t.number,
    limit: t.number,
    play_position: enumCodec(PlayPositions),
    game_mode: enumCodec(GameModes),
    session_type: enumCodec(PlaySessionTypes),
    is_completed: t.boolean,
    skip_user_details: t.boolean,
    user_id: t.string,
    challenge_id: t.number,
    playlist_id: t.number,
    country_code: t.string,
    continent_code: t.string,
    age_group: enumCodec(AgeGroups)
  })
]);
export const LeaderboardResultSchema = t.intersection([
  t.type({
    doc_id: t.string,
    rank: t.number,
    score: t.number,
    leaderboard_anonymity: t.boolean,
    first: t.string,
    last: t.string,
    rating: t.number,
    avatar: t.union([t.string, t.null])
  }),
  t.partial({
    effective_from_date: timestampCodec,
    effective_to_date: timestampCodec
  })
]);
export const GetLeaderboardResponseSchema = t.array(LeaderboardResultSchema);

export type GetLeaderboardRequest = t.TypeOf<typeof GetLeaderboardRequestSchema>;
export type LeaderboardResult = t.TypeOf<typeof LeaderboardResultSchema>;
export type GetLeaderboardResponse = t.TypeOf<typeof GetLeaderboardResponseSchema>;

export const RefreshLeaderboardScoresRequestSchema = t.type({
  period: enumCodec(PeriodType),
  game_mode: enumCodec(GameModes)
});
export const RefreshLeaderboardScoresResponseSchema = t.void;

export type RefreshLeaderboardScoresRequest = t.TypeOf<typeof RefreshLeaderboardScoresRequestSchema>;
export type RefreshLeaderboardScoresResponse = t.TypeOf<typeof RefreshLeaderboardScoresResponseSchema>;

// Client API
export const StatsApis = {
  Stats: {
    GetUserStats: {
      Name: "stats-consistentMetrics-getUserStats",
      Request: GetUserStatsRequestSchema,
      Response: GetUserStatsResponseSchema,
    },
    GetLeaderboard: {
      Name: "stats-challengeBybIqFix-getLeaderboard",
      Request: GetLeaderboardRequestSchema,
      Response: GetLeaderboardResponseSchema,
    },
    RefreshLeaderboardScores: {
      Name: "stats-refreshLeaderboardScores",
      Request: RefreshLeaderboardScoresRequestSchema,
      Response: RefreshLeaderboardScoresResponseSchema,
    }
  },
};
